import React from 'react'
import { observer } from 'mobx-react-lite'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import variables from 'variables/variables'
import Announcements from 'features/main/main-router/org-guard/main-container/announcement/Announcements'
import { SxProps } from '@mui/material/styles'
import { Theme } from '@mui/material/styles'
import useWindowSize from 'reusable/hooks/useWindowSize'

export interface AppBarContainerProps {
   title?: React.ReactNode
   /** Use this if you want to override the typography */
   customTitle?: React.ReactNode
   leftButtonIcon?: React.ReactNode
   rightButtons?: React.ReactNode
   noContentPadding?: boolean
   sx?: SxProps<Theme>
   appBarSx?: SxProps<Theme>
   appBarProps?: React.ComponentProps<typeof AppBar>
   children?: React.ReactNode
   centredBanners?: React.ReactNode[]
}

export const AppBarContainer = observer(function AppBarContainer(props: AppBarContainerProps) {
   const { screenLayout, width } = useWindowSize()

   return (
      <Stack sx={{ height: '100vh', position: 'relative', ...props.sx }}>
         <Box>
            <Announcements />
            <AppBar sx={{ position: 'static', ...props.appBarSx }} {...props.appBarProps}>
               <Toolbar sx={{ display: 'flex' }}>
                  <Box sx={{ flex: 1, ml: '-6px' }}>
                     {!!props.leftButtonIcon && (
                        <IconButton
                           id="navigation-menu"
                           edge="start"
                           color="inherit"
                           aria-label="Menu"
                           size="medium"
                        >
                           {props.leftButtonIcon}
                        </IconButton>
                     )}
                     {props.customTitle && <>{props.customTitle}</>}
                     {props.title && <Typography
                        color="inherit"
                        variant="h6"
                        sx={{ flexGrow: 1 }}
                     >
                        {props.title}
                     </Typography>}
                  </Box>

                  <Stack direction="row">
                     {props.rightButtons}
                  </Stack>
               </Toolbar>
            </AppBar>
         </Box>
         {!!props.centredBanners?.length && <Stack sx={{
            marginTop: 2,
            alignItems: 'center',
            padding: screenLayout === 'mobile' ? 0.5 : variables.contentPadding,
         }}>
            {props.centredBanners}
         </Stack>}
         <Stack sx={{
            padding: props.noContentPadding ? 0 : screenLayout === 'mobile' ? 0.5 : variables.contentPadding,
            flexGrow: 1,
            overflowY: 'auto', // Add scrollbar only to this container,
            scrollbarGutter: 'stable', // Reserve the scrollbar's space even when not scrolling, so centred content doesn't shift when it appears
            position: 'relative',
            '& > :last-child': {
               flexGrow: 1,
            },
            // Just about all the pages wont work for mobile, the content is far to wide and will need to be adjusted
            // This is here so the pages will work, but should be removed once those pages are updated and made responsive
            ...(screenLayout === 'mobile' ? { width: `${width}px` } : {})
         }}
         >
            {props.children}
         </Stack>
      </Stack >
   )
})
